<template>
  <AccountContextWrapper>
    <!-- TODO request being added to an account logic -->
    <!-- TODO on any change made to an account, make sure to refetch accounts to ensure we have the most up-to-date account data (e.g. if we add a new account, we want to make sure it shows up in the UI immediately without requiring a page refresh) -->
    <template #default>
      <bPanel title="Account Management" tag="article" class="mt-3">
        <div v-if="loadingAccounts" class="text-center my-4">
          <BSpinner label="Loading accounts..." />
        </div>
        <template v-else>
          <BDropdown text="Manage Accounts" variant="outline-secondary">
            <BDropdownItem
              :disabled="isAnyAcctLoading"
              @click="handleCreateAccountClick"
            >
              <IconPlus class="me-2" :size="16" />
              Add Account
            </BDropdownItem>
            <BDropdownDivider v-if="accounts.length > 0" />
            <BDropdownItem
              v-for="account in accounts"
              :key="account.acct_uid"
              :disabled="isAnyAcctLoading"
              @click="handleEditAccountClick(account)"
            >
              <BSpinner
                v-if="loadingAcctsByUid[account.acct_uid]"
                small
                class="me-2"
              />
              <IconEdit v-else class="me-2" :size="16" />
              {{ account.acct_name }}
            </BDropdownItem>
          </BDropdown>

          <div v-if="isCreateMode || isEditMode" class="mt-3">
            <div class="d-flex justify-content-end mb-2">
              <BButton
                size="sm"
                variant="outline-secondary"
                @click="clearActiveMode"
              >
                Close
              </BButton>
            </div>
          </div>
        </template>
      </bPanel>

      <AccountsCreateView v-if="isCreateMode" />
      <AccountsEditView v-else-if="isEditMode" :acct-uid="activeAcctUid" />
    </template>
  </AccountContextWrapper>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  userAccount as userAcctNs,
  userSession as userSessNs,
} from "@/store/namespaces.js";
import {
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BSpinner,
  BButton,
} from "bootstrap-vue-next";
import bPanel from "@/components/atoms/bPanel.vue";
import { IconPlus, IconEdit } from "@tabler/icons-vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import AccountsCreateView from "@/components/Accounts/CreateView.vue";
import AccountsEditView from "@/components/Accounts/EditView.vue";

export default {
  name: "IndexView",
  components: {
    bPanel,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BSpinner,
    BButton,
    IconPlus,
    IconEdit,
    AccountContextWrapper,
    AccountsCreateView,
    AccountsEditView,
  },
  data() {
    return {
      activeMode: null,
      activeAcctUid: null,
    };
  },
  computed: {
    ...mapGetters({
      accounts: `${userAcctNs}/getAccounts`,
      loadingAccounts: `${userAcctNs}/getLoadingAccounts`,
      loadingAcctsByUid: `${userAcctNs}/getLoadingAcctsByUid`,
    }),
    isAnyAcctLoading() {
      return Object.values(this.loadingAcctsByUid).some(Boolean);
    },
    isCreateMode() {
      return this.activeMode === "create";
    },
    isEditMode() {
      return this.activeMode === "edit" && Boolean(this.activeAcctUid);
    },
  },
  methods: {
    ...mapActions({
      fetchAcctByUid: `${userAcctNs}/fetchAcctByUid`,
      getUserInfo: `${userSessNs}/getUserInfo`,
    }),
    clearActiveMode() {
      this.activeMode = null;
      this.activeAcctUid = null;
    },
    handleCreateAccountClick() {
      if (this.isAnyAcctLoading) {
        return;
      }

      this.activeMode = "create";
      this.activeAcctUid = null;
    },
    async handleEditAccountClick(account) {
      if (this.isAnyAcctLoading) {
        return;
      }

      const acctUid = account?.acct_uid;
      if (!acctUid) {
        console.warn("Cannot edit account because account UID is undefined.");
        return;
      }

      const userInfo = await this.getUserInfo();
      const userUid = userInfo?.id;
      if (!userUid) {
        console.warn("Cannot edit account because user UID is undefined.");
        return;
      }

      const fetchedAccount = await this.fetchAcctByUid({ userUid, acctUid });
      if (!fetchedAccount) {
        return;
      }

      this.activeMode = "edit";
      this.activeAcctUid = acctUid;
    },
  },
};
</script>
